import Phaser from 'phaser';
import { SCOOP_STACK_DY, SEAT_RISE, BASE_SEAT_ORIGIN } from '../config.ts';
import type { BaseId, Order } from '../levels.ts';

/** Logical display width of a scoop on the counter (art is 200×175). */
const SCOOP_W = 132;
const SCOOP_H = SCOOP_W * (175 / 200);

/** Display size + how far above the top scoop's center each topping perches. */
const TOPPING: Record<string, { w: number; h: number; dy: number }> = {
  top_cherry: { w: 66, h: 78, dy: 42 },
  top_sprinkles: { w: 98, h: 83, dy: 16 },
  top_wafer: { w: 88, h: 74, dy: 40 },
  top_whip: { w: 78, h: 98, dy: 50 },
};

/**
 * The ice cream being built: a base plus a vertical stack of scoops (and an
 * optional topping). The container's origin (0,0) is the base's "seat" — the
 * opening where the first scoop nestles — so scoops just stack upward from
 * there regardless of which base is used.
 */
export class IceCream extends Phaser.GameObjects.Container {
  private baseSprite!: Phaser.GameObjects.Image;
  private scoops: Phaser.GameObjects.Image[] = [];
  private scoopFlavors: string[] = [];
  private topping: Phaser.GameObjects.Image | null = null;
  private toppingKey: string | null = null;
  private baseId: BaseId = 'cone';

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.setBase('cone');
  }

  setBase(base: BaseId): void {
    this.baseId = base;
    if (this.baseSprite) this.baseSprite.destroy();
    const key = base === 'cup' ? 'base_cup' : 'base_cone';
    this.baseSprite = this.scene.add.image(0, 0, key);
    const w = base === 'cup' ? 170 : 160;
    const h = base === 'cup' ? 190 : 200;
    this.baseSprite.setDisplaySize(w, h);
    this.baseSprite.setOrigin(0.5, BASE_SEAT_ORIGIN[base]);
    this.addAt(this.baseSprite, 0);
  }

  /** Y of the center of scoop at stack index i (0 = bottom). */
  private scoopY(i: number): number {
    return -SEAT_RISE - i * SCOOP_STACK_DY;
  }

  get scoopCount(): number {
    return this.scoops.length;
  }

  /** Add one scoop of `flavor`; returns the new total scoop count. */
  addScoop(flavor: string): number {
    const i = this.scoops.length;
    const targetY = this.scoopY(i);
    const s = this.scene.add.image(0, targetY, `scoop_${flavor}`);
    s.setDisplaySize(SCOOP_W, SCOOP_H);
    // New scoop sits just under the topping; keep base at the very back.
    this.add(s);
    this.scoops.push(s);
    this.scoopFlavors.push(flavor);
    this.repositionTopping();

    // Drop-in with a soft bounce + squash pop.
    s.setY(targetY - 220);
    s.setScale(s.scaleX * 1.1, s.scaleY * 0.8);
    this.scene.tweens.add({
      targets: s,
      y: targetY,
      duration: 360,
      ease: 'Bounce.Out',
    });
    this.scene.tweens.add({
      targets: s,
      scaleX: SCOOP_W / s.width,
      scaleY: SCOOP_H / s.height,
      duration: 360,
      ease: 'Back.Out',
    });
    return this.scoops.length;
  }

  removeLast(): void {
    const s = this.scoops.pop();
    this.scoopFlavors.pop();
    if (!s) return;
    this.scene.tweens.add({
      targets: s,
      y: s.y - 180,
      alpha: 0,
      scale: 0,
      duration: 220,
      ease: 'Back.In',
      onComplete: () => s.destroy(),
    });
    this.repositionTopping();
  }

  setTopping(toppingKey: string | null): void {
    if (this.topping) {
      this.topping.destroy();
      this.topping = null;
    }
    this.toppingKey = toppingKey;
    if (!toppingKey) return;
    this.topping = this.scene.add.image(0, 0, toppingKey);
    const t = TOPPING[toppingKey];
    if (t) this.topping.setDisplaySize(t.w, t.h);
    this.add(this.topping);
    this.repositionTopping();
  }

  private repositionTopping(): void {
    if (!this.topping || !this.toppingKey) return;
    const topY = this.scoops.length ? this.scoopY(this.scoops.length - 1) : -SEAT_RISE;
    const dy = TOPPING[this.toppingKey]?.dy ?? SCOOP_H * 0.32;
    this.topping.setPosition(0, topY - dy);
    this.bringToTop(this.topping);
  }

  /** Y of the top of the stack, for placing rewards (e.g. a star) above it. */
  topOfStack(): number {
    const n = Math.max(this.scoops.length, 1);
    return this.scoopY(n - 1) - SCOOP_H / 2;
  }

  /** Map of flavor -> scoop count currently built. */
  counts(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const f of this.scoopFlavors) out[f] = (out[f] ?? 0) + 1;
    return out;
  }

  clearScoops(): void {
    for (const s of this.scoops) s.destroy();
    this.scoops = [];
    this.scoopFlavors = [];
    this.setTopping(null);
  }

  /** Does the built ice cream satisfy the order (right flavors & counts)? */
  matches(order: Order): boolean {
    const built = this.counts();
    const want: Record<string, number> = {};
    for (const r of order.scoops) want[r.flavor] = (want[r.flavor] ?? 0) + r.count;
    const keys = new Set([...Object.keys(built), ...Object.keys(want)]);
    for (const k of keys) {
      if ((built[k] ?? 0) !== (want[k] ?? 0)) return false;
    }
    return this.baseId === order.base;
  }

  /** A gentle wobble for "not quite — try again" (never a harsh shake). */
  wobble(): void {
    this.scene.tweens.add({
      targets: this,
      angle: { from: -6, to: 6 },
      duration: 90,
      yoyo: true,
      repeat: 3,
      ease: 'Sine.InOut',
      onComplete: () => this.setAngle(0),
    });
  }
}
